import {
	type AssetAccessLevel,
	type ElasoRoleRef,
	getRoleKey,
} from "@elaso/shared/types/authorization";
import { useStore } from "zustand";
import { authStore } from "@/lib/stores/auth-store";
import { type ChannelEntry, coreStore } from "@/lib/stores/core-store";

export type PermissionLevel = AssetAccessLevel;

export const toOpenFgaKey = getRoleKey;

export function buildOpenFgaKeySet(roles: ElasoRoleRef[]): Set<string> {
	const keys = new Set<string>();
	for (const role of roles) {
		keys.add(getRoleKey(role));
	}
	return keys;
}

/**
 * Whether the caller has any access to this channel.
 *
 * Reads the SERVER-RESOLVED `channel.selfPermission`, computed server-side as
 * the caller's roles intersected with the channel ACL and sent with channel.list
 * (docs/adr/24-what-the-channel-list-sends-to-the-client.md). No client-side
 * role intersection. A channel with a non-null `selfPermission` is one the
 * caller can at least read.
 */
export function hasAccess(channel: ChannelEntry): boolean {
	return channel.selfPermission != null;
}

/**
 * Whether a channel should render in the sidebar.
 *
 * Visible when the caller has access, or when the row came from the client-side
 * cache (`isShell`) and the live channel.list has not landed yet
 * (`!channelsLive`). That second clause keeps the cached list on screen until
 * live data arrives, then goes inert.
 *
 * The bypass gates on `channelsLive` alone, not the old `rolesReady`. Filtering
 * authority used to arrive after channel.list, as the caller's roles, so the
 * cached rows had to survive that window. The authoritative per-caller
 * `selfPermission` now arrives WITH channel.list, so `channelsLive` is exactly
 * when real filtering becomes authoritative. Writes stay read-only meanwhile,
 * because `derivePermissionLevel` returns null for a cached row with no
 * `selfPermission`.
 */
export function isChannelVisible(
	channel: ChannelEntry,
	/** `channelsLive` is true once the live channel.list, with `selfPermission`, has landed. */
	channelsLive: boolean
): boolean {
	return hasAccess(channel) || (channel.isShell === true && !channelsLive);
}

export function derivePermissionLevel(
	channel: ChannelEntry
): PermissionLevel | null {
	return channel.selfPermission ?? null;
}

function filterAccessibleChannels(
	channelMap: Map<string, ChannelEntry>,
	channelsLive: boolean
): Map<string, ChannelEntry> {
	const accessible = new Map<string, ChannelEntry>();
	for (const [id, channel] of channelMap) {
		if (channel.isThread && channel.parentId) {
			const parent = channelMap.get(channel.parentId);
			if (parent && isChannelVisible(parent, channelsLive)) {
				accessible.set(id, channel);
			}
			continue;
		}

		if (isChannelVisible(channel, channelsLive)) {
			accessible.set(id, channel);
		}
	}
	return accessible;
}

export function useAccessibleChannels(): Map<string, ChannelEntry> {
	const user = useStore(authStore, (state) => state.user);
	const channelMap = useStore(coreStore, (state) => state.channels);
	const channelsLive = useStore(coreStore, (state) => state.channelsLive);

	if (!user?.uid) return new Map();

	return filterAccessibleChannels(channelMap, channelsLive);
}

export function useSelfPermissionsOnChannel(
	channelId: string
): PermissionLevel | null {
	const channel = useStore(coreStore, (state) => state.channels.get(channelId));
	return channel?.selfPermission ?? null;
}
